import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Chip,
  Divider,
  RadioGroup,
  Radio,
  LinearProgress,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  AutoFixHigh as TailorIcon,
  Language as LanguageIcon,
  School as ReadingIcon,
  Public as CultureIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as QueuedIcon,
  SkipNext as SkippedIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import useIsMobile from '../hooks/useIsMobile';

interface Student {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  grade_level?: string;
  reading_level?: number;
  primary_language?: string;
  cultures: string[];
  culture_codes?: string[];
}

interface DocumentInfo {
  id: number;
  name: string;
  description?: string;
  grade_level?: string;
  reading_level?: number;
}

interface Culture {
  id: number;
  code: string;
  name: string;
  description?: string;
}

type StudentStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'skipped';

interface StudentProgress {
  status: StudentStatus;
  message?: string;
  error?: string;
  currentPage?: number;
  totalPages?: number;
  reused?: boolean;
}

interface BulkTailorDialogProps {
  open: boolean;
  onClose: () => void;
  document: DocumentInfo | null;
  students: Student[];
  tailoringStatus: { [key: number]: any };
  onSuccess?: () => void;
}

const BulkTailorDialog: React.FC<BulkTailorDialogProps> = ({
  open,
  onClose,
  document,
  students,
  tailoringStatus,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { token, isAuthenticated } = useAuth();

  // Phase: 'settings' or 'progress'
  const [phase, setPhase] = useState<'settings' | 'progress' | 'complete'>('settings');

  // Mode toggle
  const [useStudentProfiles, setUseStudentProfiles] = useState(true);

  // Output format
  const [outputFormat, setOutputFormat] = useState<'pdf' | 'interactive'>('pdf');

  // Shared tailoring notes (applies to both modes)
  const [tailoringNotes, setTailoringNotes] = useState('');

  // Custom mode settings
  const [cultures, setCultures] = useState<Culture[]>([]);
  const [enableCulturalAdaptation, setEnableCulturalAdaptation] = useState(false);
  const [enableReadingLevelAdjustment, setEnableReadingLevelAdjustment] = useState(true);
  const [enableLanguageChange, setEnableLanguageChange] = useState(false);
  const [selectedCultures, setSelectedCultures] = useState<number[]>([]);
  const [adjustmentType, setAdjustmentType] = useState<'reading_level' | 'grade_level'>('grade_level');
  const [targetReadingLevel, setTargetReadingLevel] = useState<number>(0);
  const [targetGradeLevel, setTargetGradeLevel] = useState<string>('');
  const [targetLanguage, setTargetLanguage] = useState<string>('en');

  // Progress tracking
  const [studentProgressMap, setStudentProgressMap] = useState<{ [key: number]: StudentProgress }>({});
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  // Summary counts
  const [summaryTailored, setSummaryTailored] = useState(0);
  const [summarySkipped, setSummarySkipped] = useState(0);
  const [summaryFailed, setSummaryFailed] = useState(0);
  const [summaryReused, setSummaryReused] = useState(0);

  const abortControllerRef = useRef<AbortController | null>(null);
  const progressListRef = useRef<HTMLDivElement>(null);

  const studentsToTailor = students.filter(
    (s) => !tailoringStatus[s.id]?.has_tailored
  );
  const alreadyTailoredStudents = students.filter(
    (s) => tailoringStatus[s.id]?.has_tailored
  );

  useEffect(() => {
    if (open && token && isAuthenticated) {
      fetchCultures();
    }
  }, [open, token, isAuthenticated]);

  useEffect(() => {
    if (open) {
      setPhase('settings');
      setError(null);
      setStudentProgressMap({});
      setCurrentStudentIndex(0);
      setTotalToProcess(0);
      setIsRunning(false);
      setSummaryTailored(0);
      setSummarySkipped(0);
      setSummaryFailed(0);
      setSummaryReused(0);
      setTailoringNotes('');
    }
  }, [open]);

  useEffect(() => {
    if (progressListRef.current) {
      progressListRef.current.scrollTop = progressListRef.current.scrollHeight;
    }
  }, [studentProgressMap, currentStudentIndex]);

  const fetchCultures = async () => {
    try {
      const response = await fetch('/api/students/cultures', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch cultures');
      const data = await response.json();
      if (data.cultures) setCultures(data.cultures);
    } catch (err) {
      console.error('Error fetching cultures:', err);
    }
  };

  const handleCultureToggle = (cultureId: number) => {
    setSelectedCultures((prev) =>
      prev.includes(cultureId)
        ? prev.filter((id) => id !== cultureId)
        : [...prev, cultureId]
    );
  };

  const handleStartTailoring = useCallback(async () => {
    if (!document || !token || !isAuthenticated) return;

    if (!useStudentProfiles) {
      if (!enableCulturalAdaptation && !enableReadingLevelAdjustment && !enableLanguageChange) {
        setError(t('tailoring.errors.enableAtLeastOne'));
        return;
      }
    }

    if (studentsToTailor.length === 0) {
      setError(t('bulkTailoring.noStudentsToTailor'));
      return;
    }

    setPhase('progress');
    setIsRunning(true);
    setError(null);

    const initialProgress: { [key: number]: StudentProgress } = {};
    studentsToTailor.forEach((s) => {
      initialProgress[s.id] = { status: 'queued' };
    });
    alreadyTailoredStudents.forEach((s) => {
      initialProgress[s.id] = { status: 'skipped', message: t('bulkTailoring.alreadyTailored') };
    });
    setStudentProgressMap(initialProgress);

    const requestBody: any = {
      student_ids: studentsToTailor.map((s) => s.id),
      use_student_profiles: useStudentProfiles,
      output_format: outputFormat,
      tailoring_notes: tailoringNotes,
    };

    if (!useStudentProfiles) {
      requestBody.cultural_adaptation = enableCulturalAdaptation;
      requestBody.reading_level_adjustment = enableReadingLevelAdjustment;
      requestBody.language_change = enableLanguageChange;
      requestBody.selected_cultures = selectedCultures;
      requestBody.adjustment_type = adjustmentType;
      requestBody.target_reading_level = adjustmentType === 'reading_level' ? targetReadingLevel : null;
      requestBody.target_grade_level = adjustmentType === 'grade_level' ? targetGradeLevel : null;
      requestBody.target_language = targetLanguage;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetch(
        `/api/documents/${document.id}/bulk-tailor?token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('tailoring.errors.startFailed'));
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error(t('tailoring.errors.noStream'));
      }

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'batch_start') {
                setTotalToProcess(data.to_process);
              } else if (data.type === 'student_start') {
                setCurrentStudentIndex(data.index);
                setStudentProgressMap((prev) => ({
                  ...prev,
                  [data.student_id]: {
                    status: 'processing',
                    message: data.reused
                      ? t('bulkTailoring.reusedCopy')
                      : t('bulkTailoring.processing'),
                    reused: !!data.reused,
                  },
                }));
              } else if (data.type === 'student_progress') {
                setStudentProgressMap((prev) => ({
                  ...prev,
                  [data.student_id]: {
                    ...prev[data.student_id],
                    status: 'processing',
                    message: data.message,
                    currentPage: data.current_page,
                    totalPages: data.total_pages,
                  },
                }));
              } else if (data.type === 'student_complete') {
                setStudentProgressMap((prev) => ({
                  ...prev,
                  [data.student_id]: {
                    status: 'completed',
                    message: t('bulkTailoring.completed'),
                    reused: !!data.reused,
                  },
                }));
              } else if (data.type === 'student_error') {
                setStudentProgressMap((prev) => ({
                  ...prev,
                  [data.student_id]: {
                    status: 'failed',
                    error: data.error,
                  },
                }));
              } else if (data.type === 'batch_complete') {
                setSummaryTailored(data.tailored);
                setSummarySkipped(data.skipped);
                setSummaryFailed(data.failed);
                setSummaryReused(data.reused || 0);
                setPhase('complete');
                setIsRunning(false);
              } else if (data.type === 'error') {
                setError(data.message);
                setIsRunning(false);
                setPhase('complete');
              }
            } catch {
              // ignore parse errors for incomplete JSON
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        setPhase('complete');
        setIsRunning(false);
      } else {
        console.error('Bulk tailoring error:', err);
        setError(err.message || t('tailoring.errors.tailorFailed'));
        setIsRunning(false);
        setPhase('complete');
      }
    }
  }, [
    document, token, isAuthenticated, useStudentProfiles, outputFormat,
    tailoringNotes, enableCulturalAdaptation, enableReadingLevelAdjustment,
    enableLanguageChange, selectedCultures, adjustmentType, targetReadingLevel,
    targetGradeLevel, targetLanguage, studentsToTailor, alreadyTailoredStudents, t,
  ]);

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleClose = () => {
    if (isRunning) return;
    if (phase === 'complete' && summaryTailored > 0 && onSuccess) {
      onSuccess();
    }
    onClose();
  };

  const getStatusIcon = (status: StudentStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'failed':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
      case 'processing':
        return <CircularProgress size={18} />;
      case 'skipped':
        return <SkippedIcon sx={{ color: 'text.disabled', fontSize: 20 }} />;
      default:
        return <QueuedIcon sx={{ color: 'text.disabled', fontSize: 20 }} />;
    }
  };

  const getStatusLabel = (status: StudentStatus) => {
    switch (status) {
      case 'completed': return t('bulkTailoring.completed');
      case 'failed': return t('bulkTailoring.failed');
      case 'processing': return t('bulkTailoring.processing');
      case 'skipped': return t('bulkTailoring.skipped');
      default: return t('bulkTailoring.queued');
    }
  };

  const getLanguageName = (code?: string) => {
    if (!code) return 'English';
    return t(`students.form.languages.${code}`, code);
  };

  if (!document) return null;

  const overallProgress = totalToProcess > 0
    ? (currentStudentIndex / totalToProcess) * 100
    : 0;

  return (
    <Dialog
      open={open}
      onClose={isRunning ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      aria-label={t('bulkTailoring.title', { count: studentsToTailor.length })}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TailorIcon />
          <Typography variant="h6" component="span">
            {phase === 'complete'
              ? t('bulkTailoring.batchComplete')
              : phase === 'progress'
                ? t('bulkTailoring.progressTitle')
                : t('bulkTailoring.title', { count: studentsToTailor.length })}
          </Typography>
        </Box>
        {phase === 'settings' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {document.name}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* ========== SETTINGS PHASE ========== */}
        {phase === 'settings' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {studentsToTailor.length === 0 ? (
              <Alert severity="info">{t('bulkTailoring.allSelectedTailored')}</Alert>
            ) : (
              <>
                {/* Mode Toggle */}
                <Card sx={{ bgcolor: '#f8f9fa' }}>
                  <CardContent>
                    <ToggleButtonGroup
                      value={useStudentProfiles ? 'profiles' : 'custom'}
                      exclusive
                      onChange={(_, val) => {
                        if (val) setUseStudentProfiles(val === 'profiles');
                      }}
                      fullWidth
                      aria-label={t('bulkTailoring.useStudentProfiles')}
                    >
                      <ToggleButton value="profiles" aria-label={t('bulkTailoring.useStudentProfiles')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon />
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="body2" fontWeight={600}>
                              {t('bulkTailoring.useStudentProfiles')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                              {t('bulkTailoring.useStudentProfilesDescription')}
                            </Typography>
                          </Box>
                        </Box>
                      </ToggleButton>
                      <ToggleButton value="custom" aria-label={t('bulkTailoring.sameSettingsForAll')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SettingsIcon />
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="body2" fontWeight={600}>
                              {t('bulkTailoring.sameSettingsForAll')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                              {t('bulkTailoring.sameSettingsDescription')}
                            </Typography>
                          </Box>
                        </Box>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </CardContent>
                </Card>

                {/* Student Preview Table (profile mode) */}
                {useStudentProfiles && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t('bulkTailoring.studentPreviewHeader')} ({studentsToTailor.length})
                    </Typography>
                    {isMobile ? (
                      <Box sx={{ maxHeight: 280, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {studentsToTailor.map((student) => (
                          <Paper key={student.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                            <Typography variant="subtitle2">{student.full_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {student.grade_level || '—'} • RL: {student.reading_level || '—'} • {getLanguageName(student.primary_language)}
                            </Typography>
                            {student.cultures.length > 0 && (
                              <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                                {student.cultures.slice(0, 3).map((c) => (
                                  <Chip key={c} label={c} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                                ))}
                                {student.cultures.length > 3 && (
                                  <Chip label={`+${student.cultures.length - 3}`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                                )}
                              </Box>
                            )}
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
                        <Table size="small" stickyHeader aria-label={t('bulkTailoring.studentPreviewHeader')}>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('students.name')}</TableCell>
                              <TableCell>{t('students.gradeLevel')}</TableCell>
                              <TableCell>{t('bulkTailoring.readingLevel')}</TableCell>
                              <TableCell>{t('bulkTailoring.language')}</TableCell>
                              <TableCell>{t('bulkTailoring.cultures')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {studentsToTailor.map((student) => (
                              <TableRow key={student.id}>
                                <TableCell>{student.full_name}</TableCell>
                                <TableCell>{student.grade_level || '—'}</TableCell>
                                <TableCell>{student.reading_level || '—'}</TableCell>
                                <TableCell>{getLanguageName(student.primary_language)}</TableCell>
                                <TableCell>
                                  {student.cultures.length > 0
                                    ? student.cultures.slice(0, 2).join(', ') +
                                      (student.cultures.length > 2 ? ` +${student.cultures.length - 2}` : '')
                                    : t('bulkTailoring.noCultures')}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    {alreadyTailoredStudents.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {t('bulkTailoring.summarySkipped', { count: alreadyTailoredStudents.length })}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Custom Settings (same as TailorAssignmentDialog) */}
                {!useStudentProfiles && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Reading Level */}
                    <Card sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ReadingIcon color="primary" />
                          <Typography variant="subtitle1" fontWeight={600}>{t('tailoring.levelAdjustment')}</Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={enableReadingLevelAdjustment}
                              onChange={(e) => setEnableReadingLevelAdjustment(e.target.checked)}
                              aria-label={t('tailoring.enableLevelAdjustment')}
                            />
                          }
                          label={t('common.enable')}
                        />
                      </Box>
                      <Collapse in={enableReadingLevelAdjustment}>
                        <Box sx={{ mt: 1 }}>
                          <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                            <RadioGroup
                              value={adjustmentType}
                              onChange={(e) => setAdjustmentType(e.target.value as 'reading_level' | 'grade_level')}
                              sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}
                            >
                              <FormControlLabel
                                value="grade_level"
                                control={<Radio />}
                                label={t('tailoring.byGradeLevel')}
                              />
                              <FormControlLabel
                                value="reading_level"
                                control={<Radio />}
                                label={t('tailoring.byReadingLevel')}
                              />
                            </RadioGroup>
                          </FormControl>
                          {adjustmentType === 'reading_level' ? (
                            <TextField
                              label={t('tailoring.targetReadingLevel')}
                              type="number"
                              value={targetReadingLevel}
                              onChange={(e) => setTargetReadingLevel(parseInt(e.target.value) || 0)}
                              inputProps={{ min: 0 }}
                              sx={{ width: 250 }}
                              aria-label={t('tailoring.targetReadingLevel')}
                            />
                          ) : (
                            <FormControl sx={{ minWidth: 250 }}>
                              <InputLabel id="bulk-target-grade-label">{t('tailoring.targetGradeLevel')}</InputLabel>
                              <Select
                                labelId="bulk-target-grade-label"
                                value={targetGradeLevel}
                                onChange={(e) => setTargetGradeLevel(e.target.value)}
                                label={t('tailoring.targetGradeLevel')}
                                aria-label={t('tailoring.targetGradeLevel')}
                              >
                                {['K','1st','2nd','3rd','4th','5th','6th','7th','8th','9th','10th','11th','12th'].map((g) => (
                                  <MenuItem key={g} value={g}>{t(`students.form.grades.${g}`)}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      </Collapse>
                    </Card>

                    {/* Language */}
                    <Card sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LanguageIcon color="primary" />
                          <Typography variant="subtitle1" fontWeight={600}>{t('tailoring.languageTranslation')}</Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={enableLanguageChange}
                              onChange={(e) => setEnableLanguageChange(e.target.checked)}
                              aria-label={t('tailoring.languageTranslation')}
                            />
                          }
                          label={t('common.enable')}
                        />
                      </Box>
                      <Collapse in={enableLanguageChange}>
                        <Box sx={{ mt: 1 }}>
                          <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>{t('tailoring.targetLanguage')}</InputLabel>
                            <Select
                              value={targetLanguage}
                              onChange={(e) => setTargetLanguage(e.target.value)}
                              label={t('tailoring.targetLanguage')}
                              aria-label={t('tailoring.targetLanguage')}
                            >
                              {['en','es','fr','de','zh','vi','ar','ko','tl','pt'].map((lang) => (
                                <MenuItem key={lang} value={lang}>{t(`students.form.languages.${lang}`)}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Collapse>
                    </Card>

                    {/* Cultural Adaptation */}
                    <Card sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CultureIcon color="primary" />
                          <Typography variant="subtitle1" fontWeight={600}>Cultural Adaptation</Typography>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={enableCulturalAdaptation}
                              onChange={(e) => setEnableCulturalAdaptation(e.target.checked)}
                              aria-label="Cultural adaptation"
                            />
                          }
                          label={t('common.enable')}
                        />
                      </Box>
                      <Collapse in={enableCulturalAdaptation}>
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {cultures.map((culture) => (
                            <Chip
                              key={culture.id}
                              label={culture.name}
                              onClick={() => handleCultureToggle(culture.id)}
                              color={selectedCultures.includes(culture.id) ? 'primary' : 'default'}
                              variant={selectedCultures.includes(culture.id) ? 'filled' : 'outlined'}
                              aria-label={culture.name}
                            />
                          ))}
                        </Box>
                      </Collapse>
                    </Card>
                  </Box>
                )}

                {/* Output Format */}
                <Card sx={{ bgcolor: '#f5f0ff' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {t('tailoring.outputFormat')}
                    </Typography>
                    <RadioGroup
                      value={outputFormat}
                      onChange={(e) => setOutputFormat(e.target.value as 'pdf' | 'interactive')}
                      aria-label={t('tailoring.outputFormat')}
                    >
                      <FormControlLabel
                        value="pdf"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{t('tailoring.outputPdf')}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('tailoring.outputPdfDescription')}
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="interactive"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{t('tailoring.outputInteractive')}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('tailoring.outputInteractiveDescription')}
                            </Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </CardContent>
                </Card>

                {/* Tailoring Notes */}
                <TextField
                  label={t('tailoring.additionalNotes')}
                  value={tailoringNotes}
                  onChange={(e) => setTailoringNotes(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder={t('tailoring.additionalNotes')}
                  aria-label={t('tailoring.additionalNotes')}
                />
              </>
            )}
          </Box>
        )}

        {/* ========== PROGRESS PHASE ========== */}
        {(phase === 'progress' || phase === 'complete') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Overall Progress */}
            {phase === 'progress' && totalToProcess > 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {t('bulkTailoring.studentOfTotal', {
                      current: currentStudentIndex,
                      total: totalToProcess,
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round(overallProgress)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={overallProgress}
                  sx={{ height: 10, borderRadius: 5 }}
                  aria-label={t('bulkTailoring.progressTitle')}
                />
              </Box>
            )}

            {/* Completion Summary */}
            {phase === 'complete' && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {summaryTailored > 0 && (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={t('bulkTailoring.summaryTailored', { count: summaryTailored })}
                    color="success"
                    variant="outlined"
                  />
                )}
                {summarySkipped > 0 && (
                  <Chip
                    icon={<SkippedIcon />}
                    label={t('bulkTailoring.summarySkipped', { count: summarySkipped })}
                    color="default"
                    variant="outlined"
                  />
                )}
                {summaryReused > 0 && (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={t('bulkTailoring.summaryReused', { count: summaryReused })}
                    color="info"
                    variant="outlined"
                  />
                )}
                {summaryFailed > 0 && (
                  <Chip
                    icon={<ErrorIcon />}
                    label={t('bulkTailoring.summaryFailed', { count: summaryFailed })}
                    color="error"
                    variant="outlined"
                  />
                )}
              </Box>
            )}

            {/* Per-student status list */}
            <Box
              ref={progressListRef}
              sx={{
                maxHeight: 400,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {/* Show students being processed first, then queued, then completed, etc. */}
              {[...studentsToTailor, ...alreadyTailoredStudents].map((student) => {
                const progress = studentProgressMap[student.id];
                if (!progress) return null;
                return (
                  <Box
                    key={student.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: progress.status === 'processing' ? 'action.hover' : 'transparent',
                    }}
                  >
                    {getStatusIcon(progress.status)}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={progress.status === 'processing' ? 600 : 400} noWrap>
                        {student.full_name}
                      </Typography>
                      {progress.status === 'processing' && progress.message && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {progress.message}
                        </Typography>
                      )}
                      {progress.status === 'failed' && progress.error && (
                        <Typography variant="caption" color="error" noWrap>
                          {progress.error}
                        </Typography>
                      )}
                      {progress.status === 'skipped' && (
                        <Typography variant="caption" color="text.disabled" noWrap>
                          {t('bulkTailoring.alreadyTailored')}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {progress.reused && progress.status === 'completed'
                        ? t('bulkTailoring.reused')
                        : getStatusLabel(progress.status)}
                    </Typography>
                    {progress.status === 'processing' && !progress.reused && progress.totalPages && progress.totalPages > 0 && (
                      <Box sx={{ width: 60 }}>
                        <LinearProgress
                          variant="determinate"
                          value={((progress.currentPage || 0) / progress.totalPages) * 100}
                          sx={{ height: 4, borderRadius: 2 }}
                          aria-label={t('tailoring.processingPage', {
                            current: progress.currentPage,
                            total: progress.totalPages,
                          })}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {phase === 'settings' && (
          <>
            <Button onClick={handleClose} aria-label={t('modals.cancel')}>
              {t('modals.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleStartTailoring}
              disabled={studentsToTailor.length === 0}
              startIcon={<TailorIcon />}
              aria-label={t('bulkTailoring.startTailoring')}
            >
              {t('bulkTailoring.startTailoring')} ({studentsToTailor.length})
            </Button>
          </>
        )}
        {phase === 'progress' && (
          <Button
            onClick={handleCancel}
            color="warning"
            aria-label={t('bulkTailoring.cancel')}
          >
            {t('bulkTailoring.cancel')}
          </Button>
        )}
        {phase === 'complete' && (
          <Button
            variant="contained"
            onClick={handleClose}
            aria-label={t('bulkTailoring.done')}
          >
            {t('bulkTailoring.done')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkTailorDialog;
